// School-admin sign-up. Standalone card (PUBLIC_CHILD_PATH). Creates the school
// + admin in one call, then lands the admin in the school shell. R9.

import { useLocale, useT } from '@fajrarabi/i18n/react';
import { Alert, BrandLockup, Button, Field, Input } from '@fajrarabi/ui';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { LangSwitcher } from '~/components/LangSwitcher';
import { ApiError } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';
import { registerSchool } from '~/lib/school';

export const Route = createFileRoute('/$locale/school-signup')({
  component: SchoolSignUpPage,
});

function SchoolSignUpPage() {
  const t = useT();
  const { isRtl } = useLocale();
  const navigate = useNavigate();
  const { locale: rawLocale } = useParams({ from: '/$locale/school-signup' });
  const locale = asLocale(rawLocale);
  const { refresh } = useAuth();

  const [schoolName, setSchoolName] = useState('');
  const [adminDisplayName, setAdminDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState('QA');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await registerSchool({
        email,
        password,
        adminDisplayName,
        schoolName,
        country: country.toUpperCase(),
        locale,
      });
      await refresh();
      await navigate({ to: '/$locale', params: { locale } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('app.common.error_generic'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-dvh bg-cream-50">
      <header className="flex items-center justify-between px-6 py-5">
        <BrandLockup label={t('app.common.app_name')} />
        <LangSwitcher locale={locale} ariaLabel={t('app.common.lang_switch_label')} />
      </header>

      <main className="grid place-items-center px-6 pb-16 pt-6">
        <div className="w-full max-w-md">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl bg-white p-8 shadow-[0_24px_60px_-30px_rgba(31,26,23,0.18)] ring-1 ring-burgundy-500/10"
            dir={isRtl ? 'rtl' : 'ltr'}
          >
            <h1 className="text-2xl font-bold text-ink-900">{t('app.school.signup_title')}</h1>
            <p className="mt-1 text-sm text-ink-700">{t('app.school.signup_subtitle')}</p>

            <div className="mt-6 space-y-4">
              <Field label={t('app.school.school_name')}>
                {(id) => (
                  <Input
                    id={id}
                    required
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('app.school.admin_name')}>
                {(id) => (
                  <Input
                    id={id}
                    required
                    autoComplete="name"
                    value={adminDisplayName}
                    onChange={(e) => setAdminDisplayName(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('app.auth.email_label')}>
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('app.auth.password_label')} hint={t('app.auth.password_hint_parent')}>
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('app.school.country')}>
                {(id) => (
                  <Input
                    id={id}
                    required
                    maxLength={2}
                    value={country}
                    onChange={(e) => setCountry(e.target.value.toUpperCase())}
                    className="uppercase"
                  />
                )}
              </Field>
            </div>

            {error && (
              <Alert tone="danger" className="mt-4">
                {error}
              </Alert>
            )}

            <Button type="submit" loading={submitting} fullWidth className="mt-6">
              {t('app.school.create_account')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-ink-700">
            <Link
              to="/$locale"
              params={{ locale }}
              className="font-semibold text-burgundy-500 hover:text-burgundy-600"
            >
              {t('app.school.have_account')}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
